
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { ProfileHeader } from "@/components/profile/ProfileHeader";
import { ProfileReviews } from "@/components/profile/ProfileReviews";
import { useIsMobile } from "@/hooks/use-mobile";
import type { Review } from "@/types/review";
import { ProfileFavorites } from "@/components/profile/ProfileFavorites";
import { motion } from "framer-motion";
import { Profile } from "@/types/profile";

export default function UserProfile() {
  const params = useParams<{ id: string }>();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCurrentUser, setIsCurrentUser] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const checkCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setIsCurrentUser(user?.id === params.id);
    };
    checkCurrentUser();
  }, [params.id]);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!params.id) {
        console.error('No user ID provided');
        setIsLoading(false);
        return;
      }

      try {
        console.log('Fetching data for user ID:', params.id);
        
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('username, display_name, avatar_url, link_one, link_two, verification_status, is_creative')
          .eq('id', params.id)
          .single();

        if (profileError) {
          console.error('Profile fetch error:', profileError);
          throw profileError;
        }

        if (!profileData) {
          console.error('No profile found for ID:', params.id);
          setIsLoading(false);
          return;
        }

        // Type assertion to handle the Supabase response
        setProfile(profileData as Profile);

        const { data: reviewsData, error: reviewsError } = await supabase
          .from('reviews')
          .select('*')
          .eq('user_id', params.id)
          .order('created_at', { ascending: false });

        if (reviewsError) {
          console.error('Reviews fetch error:', reviewsError);
          throw reviewsError;
        }

        setReviews(reviewsData || []);
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setTimeout(() => {
          setIsLoading(false);
        }, 300);
      }
    };

    setIsLoading(true);
    fetchUserData();
  }, [params.id]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[70vh]">
        <Loader2 className="w-8 h-8 animate-spin opacity-70" />
      </div>
    );
  }

  if (!profile) return null;

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className={`container mx-auto ${isMobile ? 'px-2' : 'px-4'} py-8`}
    >
      <ProfileHeader
        username={profile.username || ""}
        displayName={profile.display_name}
        avatarUrl={profile.avatar_url}
        linkOne={profile.link_one}
        linkTwo={profile.link_two}
        isUpdatingAvatar={false}
        onImageSelect={() => {}} // Disabled for other users' profiles
        verificationStatus={profile.verification_status}
        isCreative={profile.is_creative}
      />

      <div className="mt-8 space-y-8">
        {isCurrentUser && (
          <section>
            <ProfileFavorites userId={params.id} />
          </section>
        )}

        <section>
          <ProfileReviews
            reviews={reviews}
            userDetails={{
              username: profile.username,
              avatar_url: profile.avatar_url,
              link_one: profile.link_one,
              link_two: profile.link_two,
              verification_status: profile.verification_status,
              is_creative: profile.is_creative
            }}
          />
        </section>
      </div>
    </motion.div>
  );
}
